import {
  MarketingCommerceItem,
  MarketingJourneyContext,
  MarketingProvider,
} from "./marketing.models";

function publicText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = [...value.replace(/<[^>]*>/g, "")]
    .filter((char) => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127)
    .join("")
    .trim()
    .slice(0, 200);
  return text || undefined;
}

// Copy only public commerce fields before asynchronous receipt/configuration lookup.
export function commerceContext(
  context?: MarketingJourneyContext,
): MarketingJourneyContext {
  const result: MarketingJourneyContext = {
    event_name: publicText(context?.event_name),
    event_category: publicText(context?.event_category),
  };
  const currency = context?.currency;
  if (currency === undefined || /^[A-Z]{3}$/.test(currency))
    result.currency = currency ?? "BRL";
  if (Number.isSafeInteger(context?.value_cents) && context!.value_cents! >= 0)
    result.value_cents = context!.value_cents;
  const items = context?.items;
  if (!Array.isArray(items) || !items.length || items.length > 100)
    return result;
  const copy: MarketingCommerceItem[] = [];
  let units = 0;
  let total = 0;
  for (const item of items) {
    if (
      !item ||
      !Number.isSafeInteger(item.ticket_id) ||
      item.ticket_id <= 0 ||
      !Number.isSafeInteger(item.quantity) ||
      item.quantity <= 0 ||
      !Number.isSafeInteger(item.unit_price_cents) ||
      item.unit_price_cents < 0
    )
      return result;
    units += item.quantity;
    total += item.quantity * item.unit_price_cents;
    if (!Number.isSafeInteger(units) || !Number.isSafeInteger(total))
      return result;
    copy.push({
      ticket_id: item.ticket_id,
      quantity: item.quantity,
      unit_price_cents: item.unit_price_cents,
      name: publicText(item.name),
      category: publicText(item.category),
      variant: publicText(item.variant),
    });
  }
  result.items = copy;
  return result;
}

export function commercePayload(
  provider: MarketingProvider,
  eventId: number,
  context: MarketingJourneyContext,
  event:
    | "event_viewed"
    | "cart_item_added"
    | "checkout_started"
    | "purchase_confirmed",
  purchaseUnits?: number,
): Record<string, unknown> {
  const items = context.items;
  const units = items?.reduce((sum, item) => sum + item.quantity, 0);
  const valueCents =
    event === "cart_item_added"
      ? items?.reduce(
          (sum, item) => sum + item.quantity * item.unit_price_cents,
          0,
        )
      : event === "event_viewed"
        ? undefined
        : context.value_cents;
  const money =
    valueCents !== undefined && context.currency
      ? { value: valueCents / 100, currency: context.currency }
      : {};
  const itemId = (item: MarketingCommerceItem) =>
    `event_${eventId}_ticket_${item.ticket_id}`;
  if (provider === "google") {
    return {
      ...money,
      items: items?.map((item) => ({
        id: itemId(item),
        item_id: itemId(item),
        google_business_vertical: "custom",
        ...(item.name ? { item_name: item.name } : {}),
        ...(item.category || context.event_category
          ? { item_category: item.category || context.event_category }
          : {}),
        ...(item.variant ? { item_variant: item.variant } : {}),
        price: item.unit_price_cents / 100,
        quantity: item.quantity,
      })) ?? [
        {
          id: `event_${eventId}`,
          item_id: `event_${eventId}`,
          google_business_vertical: "custom",
          ...(context.event_name ? { item_name: context.event_name } : {}),
          ...(context.event_category
            ? { item_category: context.event_category }
            : {}),
        },
      ],
    };
  }
  return {
    content_ids: items?.map(itemId) ?? [`event_${eventId}`],
    content_type: "product",
    ...(context.event_name ? { content_name: context.event_name } : {}),
    ...(context.event_category
      ? { content_category: context.event_category }
      : {}),
    ...(items
      ? {
          contents: items.map((item) => ({
            id: itemId(item),
            quantity: item.quantity,
            item_price: item.unit_price_cents / 100,
          })),
        }
      : {}),
    ...money,
    ...((purchaseUnits ?? units) !== undefined && event !== "event_viewed"
      ? { num_items: purchaseUnits ?? units }
      : {}),
  };
}
